import { SendIcon } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useState, useEffect } from "react";
import { toast } from "@/components/ui/sonner";
import emailjs from '@emailjs/browser';
import { trackEvent } from '@/hooks/useAnalytics';

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Load Calendly script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);
    
    const link = document.createElement('link');
    link.href = 'https://assets.calendly.com/assets/external/widget.css';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    
    return () => {
      document.body.removeChild(script);
      document.head.removeChild(link);
    };
  }, []);
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    const form = e.currentTarget;
    
    // Track form submission attempt
    trackEvent('contact_form_submit', {
      event_category: 'conversion',
      event_label: 'Contact Form Submission',
      page_section: 'contact_form'
    });
    
    try {
      emailjs.init("YOUR_PUBLIC_KEY");
      const result = await emailjs.sendForm('service_5wgnx8i', 'template_5vw2tmz', form, '8pZWfvMHB1tGXBS9Q');
      if (result.text === 'OK') {
        // Track successful submission
        trackEvent('contact_form_success', {
          event_category: 'conversion',
          event_label: 'Contact Form Success',
          page_section: 'contact_form'
        });
        toast.success("Your request has been submitted successfully. We'll be in touch soon!");
        form.reset();
      } else {
        throw new Error('Failed to send email');
      }
    } catch (error) {
      // Track form error
      trackEvent('contact_form_error', {
        event_category: 'conversion',
        event_label: 'Contact Form Error',
        page_section: 'contact_form'
      });
      toast.error("There was a problem sending your request. Please try again or contact us directly.");
      console.error("Error sending email:", error);
    } finally {
      setIsSubmitting(false);
    }
  };
  return <section id="contact" className="section bg-grayLight">
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-darkText">
            Let's Build Something That <span className="text-cyanPrimary">Matters</span>
          </h2>
          <p className="text-lg text-darkText/80 leading-relaxed">If you're building a product that can make a difference - and you need a product leader to help you do it right - let's talk.</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-stretch">
          <div className="bg-white p-8 md:p-12 rounded-2xl shadow-card">
            <h3 className="text-2xl font-semibold mb-6 text-darkText">Let's Talk</h3>
            
            {/* Primary CTA - Calendar Booking */}
            <div className="mb-8">
              <button 
                onClick={() => {
                  trackEvent('calendly_click', {
                    event_category: 'conversion',
                    event_label: 'Book Consultation Button',
                    page_section: 'contact_form'
                  });
                  window.Calendly && window.Calendly.initPopupWidget({url: 'https://calendly.com/addyspiller/30-meeting?hide_event_type_details=1&hide_gdpr_banner=1'});
                }}
                className="bg-cyanPrimary hover:bg-cyanLight text-white w-full py-4 rounded-xl text-base font-medium shadow-soft hover:shadow-md transition-all duration-300 transform hover:-translate-y-1 mb-3"
              >
                Book a Free 30-Minute Consultation
              </button>
              <p className="text-sm text-darkText/60 text-center">Most convenient - no back and forth needed</p>
            </div>

            {/* Divider */}
            <div className="flex items-center mb-6">
              <div className="flex-grow h-px bg-grayMedium/30"></div>
              <span className="px-4 text-sm text-darkText/60">Or send a quick message</span>
              <div className="flex-grow h-px bg-grayMedium/30"></div>
            </div>
            
            {/* Secondary - Simple Contact Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2 text-left">
                  <Label htmlFor="firstName" className="font-medium text-cyanPrimary mb-2 block text-left">First Name</Label>
                  <Input id="firstName" name="firstName" placeholder="Jane" required className="bg-grayLight border-grayMedium text-darkText placeholder:text-grayText/50 rounded-xl" />
                </div>
                <div className="space-y-2 text-left">
                  <Label htmlFor="lastName" className="font-medium text-cyanPrimary mb-2 block text-left">Last Name</Label>
                  <Input id="lastName" name="lastName" placeholder="Doe" required className="bg-grayLight border-grayMedium text-darkText placeholder:text-grayText/50 rounded-xl" />
                </div>
              </div>
              
              <div className="space-y-2 text-left">
                <Label htmlFor="email" className="font-medium text-cyanPrimary mb-2 block text-left">Email</Label>
                <Input id="email" name="email" type="email" placeholder="jane.doe@example.com" required className="bg-grayLight border-grayMedium text-darkText placeholder:text-grayText/50 rounded-xl" />
              </div>
              
              <div className="space-y-2 text-left">
                <Label htmlFor="message" className="font-medium text-cyanPrimary mb-2 block text-left">How can I help?</Label>
                <Textarea id="message" name="message" placeholder="Brief description of your product challenge..." className="min-h-[100px] bg-grayLight border-grayMedium text-darkText placeholder:text-grayText/50 rounded-xl" required />
              </div>
              
              <Button type="submit" className="bg-grayMedium hover:bg-grayMedium/70 text-darkText w-full py-4 rounded-xl" disabled={isSubmitting}>
                {isSubmitting ? <>Sending...</> : <>Send Message</>}
              </Button>
            </form>
          </div>
          
          <div className="bg-gradient-to-br from-white to-grayLight p-8 md:p-12 rounded-2xl text-darkText shadow-card flex flex-col">
            <h3 className="text-2xl font-semibold mb-6 text-darkText">The Process</h3>
            
            <div className="space-y-4 flex-grow">
              <div className="bg-white/80 p-4 rounded-xl border border-cyanPrimary/20 shadow-soft">
                <h4 className="font-medium mb-2 text-cyanPrimary">Initial Consultation</h4>
                <p className="text-darkText/90">We’ll kick things off with a free 30-minute call to discuss your product goals, current challenges, and where you need support. No prep required.</p>
              </div>
              
              <div className="bg-white/80 p-4 rounded-xl border border-cyanPrimary/20 shadow-soft">
                <h4 className="font-medium text-cyanPrimary mb-2">Strategy Working Session</h4>
                <p className="text-darkText/90">A focused deep dive into your product vision, target customer, and key opportunity areas - designed to clarify direction and align on priorities.</p>
              </div>
              
              <div className="bg-white/80 p-4 rounded-xl border border-cyanPrimary/20 shadow-soft">
                <h4 className="font-medium text-cyanPrimary mb-2">Get to Work</h4>
                <p className="text-darkText/90">With strategy aligned and scope defined, we dive into execution - tailoring the engagement to fit your goals, timeline, and stage.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
export default Contact;